import { useStep } from '../layout';
import { Form, FormField, FormControl, FormItem } from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useForm, useFormState } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { SectionCard } from '@/components/section-card';
import { MultiSelect } from '@/routes/trackings/components/multi-select-dropdown';
import { useNavigate } from 'react-router-dom';
import { FormError } from '@/components/form-error';
import { AudienceStepSkeleton } from '@/components/ui/skeletons';
import { useCampaignWizard } from '../context/campaign-wizard-context';
import { useEffect } from 'react';
import { SplitConfiguration } from '../../components/split-configuration';
import {
  audienceFormSchema,
  type AudienceFormValues,
  determineSplitType,
  mapVariantsToFormState,
  mapFormStateToVariants,
  DEFAULT_SPLIT_CONFIG,
  mapSegmentIdsToFormSegments,
  mapSegmentsToMultiSelectOptions
} from '../utils/audience';
import { useSegments } from '@/hooks/use-segments';

export function AudienceStep() {
  const { setStepValid } = useStep();
  const navigate = useNavigate();
  const {
    campaign,
    loading: campaignLoading,
    updateSegments,
    updateVariants,
    isSaving
  } = useCampaignWizard();
  const { segments: availableSegments, loading: segmentsLoading } = useSegments();

  // Initialize form with current campaign values
  const form = useForm<AudienceFormValues>({
    mode: 'onChange',
    resolver: zodResolver(audienceFormSchema),
    values: {
      segments: mapSegmentIdsToFormSegments(campaign?.segments.include ?? [], availableSegments),
      exclusionSegments: mapSegmentIdsToFormSegments(
        campaign?.segments.exclude ?? [],
        availableSegments
      ),
      smartSend: false,
      splitConfig: campaign?.variants.length
        ? {
            isEnabled: true,
            splitType: determineSplitType(campaign.variants),
            variants: mapVariantsToFormState(campaign.variants)
          }
        : DEFAULT_SPLIT_CONFIG
    }
  });

  // Use useFormState to watch isValid
  const { isValid } = useFormState({
    control: form.control
  });

  // Update step validity when form state changes
  useEffect(() => {
    setStepValid(isValid);
  }, [isValid, setStepValid]);

  const handleSegmentsChange = (values: string[]) => {
    const selectedSegments = mapSegmentIdsToFormSegments(values, availableSegments);

    // Update form immediately (optimistic)
    form.setValue('segments', selectedSegments, {
      shouldValidate: true
    });

    // Fire API call in background
    void updateSegments(
      selectedSegments.map((s) => s.id),
      form.getValues('exclusionSegments').map((s) => s.id)
    );
  };

  const handleExclusionSegmentsChange = (values: string[]) => {
    const selectedSegments = mapSegmentIdsToFormSegments(values, availableSegments);

    // Update form immediately (optimistic)
    form.setValue('exclusionSegments', selectedSegments, {
      shouldValidate: true
    });

    void updateSegments(
      form.getValues('segments').map((s) => s.id),
      selectedSegments.map((s) => s.id)
    );
  };

  const handleCreateSegment = () => {
    void navigate('/segments/create');
  };

  const handleSplitConfigChange = (config: NonNullable<AudienceFormValues['splitConfig']>) => {
    // Update form
    form.setValue('splitConfig', config, { shouldValidate: true });

    // Update campaign variants
    if (!config.isEnabled) {
      void updateVariants([]);
    } else {
      void updateVariants(mapFormStateToVariants(config.variants));
    }
  };

  // Use helper to generate MultiSelect options
  const multiSelectOptions = mapSegmentsToMultiSelectOptions(availableSegments);

  if (campaignLoading || segmentsLoading) {
    return <AudienceStepSkeleton />;
  }

  return (
    <div className="space-y-6">
      <Form {...form}>
        <form className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column - Audience Selection */}
            <div className="space-y-6">
              <SectionCard title="Audience">
                {/* Send to section */}
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="segments"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <Label>Send to</Label>
                        <FormControl>
                          <MultiSelect
                            options={multiSelectOptions.filter(
                              (option) =>
                                !form
                                  .getValues('exclusionSegments')
                                  .some((s: { id: string }) => s.id === option.value)
                            )}
                            value={field.value.map((s: { id: string }) => s.id)}
                            onValueChange={handleSegmentsChange}
                            placeholder="Select segments to send to..."
                            variant="default"
                            maxCount={5}
                            allowCustomAdditions={false}
                            onCustomAction={handleCreateSegment}
                            customActionLabel="Create New Segment"
                            disabled={isSaving}
                          />
                        </FormControl>
                        <FormError message={fieldState.error?.message} />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Don't send to section */}
                <div className="space-y-4 mt-6">
                  <FormField
                    control={form.control}
                    name="exclusionSegments"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <Label>Don&apos;t send to</Label>
                        <FormControl>
                          <MultiSelect
                            options={multiSelectOptions.filter(
                              (option) =>
                                !form
                                  .getValues('segments')
                                  .some((s: { id: string }) => s.id === option.value)
                            )}
                            value={field.value.map((s: { id: string }) => s.id)}
                            onValueChange={handleExclusionSegmentsChange}
                            placeholder="Select segments to exclude..."
                            variant="destructive"
                            maxCount={5}
                            allowCustomAdditions={false}
                            onCustomAction={handleCreateSegment}
                            customActionLabel="Create new Segment"
                            disabled={isSaving}
                          />
                        </FormControl>
                        <FormError message={fieldState.error?.message} />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Smart Send toggle */}
                <div className="flex items-center justify-between space-x-4 mt-6">
                  <div className="space-y-0.5">
                    <Label>Enable Smart Send</Label>
                    <div className="text-sm text-muted-foreground">
                      If enabled, subscribers who have received a message within your smart sending
                      window (8 hours) will be excluded from this send.
                    </div>
                  </div>
                  <Switch
                    checked={form.watch('smartSend')}
                    onCheckedChange={(checked) => {
                      form.setValue('smartSend', checked);
                    }}
                    disabled={isSaving}
                  />
                </div>
              </SectionCard>
            </div>

            {/* Right Column - Split Send */}
            <div className="space-y-6">
              <SplitConfiguration
                value={form.watch('splitConfig') ?? DEFAULT_SPLIT_CONFIG}
                onChange={handleSplitConfigChange}
              />
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
